import React, { useMemo } from "react";

import { FeatureFlags, Product, ProductType } from "@design-controls/types";
import {
  QFormControl,
  QHeading,
  QInput,
  QMultiSelect,
  QStack,
  QText,
  useFeatureFlag,
} from "@qualio/ui-components";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/config";

import { useTags } from "../../hooks/useTags";
import { RadioGroup } from "../RadioGroup/RadioGroup";
import { ResourceLibraryProductField } from "./ResourceLibraryProductField";

// Narrower than either consumer's schema on purpose: useFormContext<T>() is an
// unchecked assertion, so naming one would lie on the other page.
export type ProductProfileFields = Pick<Product, "name" | "type" | "tags">;

export const ProductProfile: React.FC = () => {
  const { t } = useTranslation();
  const { data: tags, isLoading: isLoadingTags } = useTags();
  const {
    formState: { errors },
  } = useFormContext<ProductProfileFields>();
  const linkResourceLibraryProductEnabled = Boolean(
    useFeatureFlag()[FeatureFlags.LINK_RESOURCE_LIBRARY_PRODUCT],
  );

  const tagOptions = useMemo(() => {
    return (tags ?? []).map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [tags]);

  return (
    <QStack gap={4} width={640}>
      <QStack>
        <QHeading size="md" fontWeight={600}>
          {t("products.profile.heading")}
        </QHeading>
        <QText fontSize="sm" fontWeight={400}>
          <QText as="span" color="red.500">
            *
          </QText>{" "}
          {t("products.profile.requiredFields")}
        </QText>
      </QStack>
      <QFormControl
        label={t("products.profile.fields.productName")}
        isRequired
        error={errors?.name?.message && t(errors.name.message)}
        isInvalid={!!errors?.name}
      >
        <Controller
          name="name"
          render={({ field: { onChange, value } }) => (
            <QInput value={value} onChange={onChange} data-cy="product-name" />
          )}
        />
      </QFormControl>
      {linkResourceLibraryProductEnabled && (
        <QFormControl
          label={t("products.profile.fields.resourceLibraryProduct")}
          helper={t("products.profile.helpers.resourceLibraryProduct")}
          data-cy="resource-library-product-select"
        >
          <ResourceLibraryProductField />
        </QFormControl>
      )}
      <QFormControl
        label={t("products.profile.fields.productType")}
        error={errors?.type?.message}
        isInvalid={!!errors?.type}
      >
        <Controller
          name="type"
          render={({ field: { value, onChange } }) => {
            const currentLanguage = i18n.language;
            const productTypeLabels: Record<ProductType, string> = {
              "Physical device only": t(
                "products.profile.types.physicalDeviceOnly",
              ),
              "Software only": t("products.profile.types.softwareOnly"),
              "Combination of physical device and software": t(
                "products.profile.types.combinationPhysicalAndSoftware",
              ),
            };

            return (
              <RadioGroup
                defaultValue={value}
                onChange={onChange}
                options={ProductType.options.map((option) => {
                  const translatedLabel = productTypeLabels[option.value];
                  // Show translation with original English in parentheses for regulatory compliance
                  const label =
                    currentLanguage !== "en" && translatedLabel !== option.value
                      ? `${translatedLabel} (${option.value})`
                      : translatedLabel;

                  return {
                    label,
                    value: option.value,
                  };
                })}
              />
            );
          }}
        />
      </QFormControl>
      <QFormControl
        label={t("products.profile.fields.documentTags")}
        helper={t("products.profile.helpers.documentTags")}
        data-cy="document-tags-multiselect"
      >
        <Controller
          name="tags"
          render={({ field: { value, onChange } }) => (
            <QMultiSelect
              options={tagOptions}
              isLoading={isLoadingTags}
              value={value}
              onChange={(values) => onChange(values.map((item) => item.value))}
            />
          )}
        />
      </QFormControl>
    </QStack>
  );
};
